import React, { useState, useEffect } from 'react';
import { Box, Grid, Grow, Typography, CircularProgress, Paper, Button } from '@mui/material';
import { Block } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SignInImg from 'Assets/Images/login.png';
import SignInForm from './SignInForm';
import API from '../../api';

export default function AdminSignIn() {
    const navigate = useNavigate();
    
    // IP access check states
    const [isCheckingAccess, setIsCheckingAccess] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [userIp, setUserIp] = useState('');

    // Check admin access on mount
    useEffect(() => {
        checkAdminAccess();
    }, []);

    const checkAdminAccess = async () => {
         setHasAccess(true);
         return
        setIsCheckingAccess(true);
        
        try {
            const response = await API.auth.checkAdminAccess();
            
            if (response.data.success && response.data.authorized) {
                setHasAccess(true);
                setUserIp(response.data.ipAddress);
                console.log('✅ Admin access granted from IP:', response.data.ipAddress);
            } else {
                setHasAccess(false);
                setUserIp(response.data.ipAddress);
                toast.error('Admin access denied from your IP address');
                setTimeout(() => navigate('/'), 5000);
            }
        } catch (error) {
            console.error('Error checking admin access:', error);
            setHasAccess(false);
            setUserIp(error.response?.data?.ipAddress || 'Unknown');
            toast.error('Admin access check failed');
            setTimeout(() => navigate('/'), 5000);
        } finally {
            setIsCheckingAccess(false);
        }
    };

    // Show loading while checking
    if (isCheckingAccess) {
        return (
            <Grid container sx={{ minHeight: '100dvh', background: 'linear-gradient(to right, #041e42, #78a8e6)', overflow: 'auto' }}>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Paper elevation={3} sx={{ p: 6, textAlign: 'center' }}>
                        <CircularProgress size={60} sx={{ mb: 3, color: '#0A48B3' }} />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#041e42', mb: 1 }}>
                            Verifying Admin Access
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Checking IP authorization...
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        );
    }

    // Show access denied
    if (!hasAccess) {
        return (
            <Grid container sx={{ minHeight: '100dvh', background: 'linear-gradient(to right, #041e42, #78a8e6)', overflow: 'auto' }}>
                <Grow in timeout={1000}>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: '#fff', px: 4, mt: 4 }}>
                        <Typography variant="h4" fontWeight="bold" gutterBottom>
                            TAS ADMIN PORTAL
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            Trusted Advisory Service
                        </Typography>
                        <Box component="img" src={SignInImg} alt="illustration" sx={{ width: '60%', maxWidth: 400, mt: 4 }} />
                    </Grid>
                </Grow>

                <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', px: 4 }}>
                    <Grow in timeout={1000}>
                        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
                            <Block sx={{ fontSize: 80, color: '#d32f2f', mb: 2 }} />
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 2 }}>
                                Access Denied
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                                Your IP address is not authorized for admin access
                            </Typography>
                            <Box sx={{ p: 2, backgroundColor: '#ffebee', borderRadius: 2, mb: 3 }}>
                                <Typography variant="body2" color="text.secondary">Your IP:</Typography>
                                <Typography variant="h6" sx={{ fontFamily: 'monospace', color: '#d32f2f' }}>
                                    {userIp}
                                </Typography>
                            </Box>
                            <Button variant="outlined" onClick={() => navigate('/')} sx={{ mb: 2 }}>
                                Go to Home
                            </Button>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Auto-redirecting in 5 seconds...
                            </Typography>
                        </Paper>
                    </Grow>
                </Grid>
            </Grid>
        );
    }

    // Show login page (access granted)
    return (
        <Grid container sx={{ minHeight: '100dvh', background: 'linear-gradient(to right, #041e42, #78a8e6)', overflow: 'auto' }}>
            <Grow in timeout={1000}>
                {/* Left side */}
                <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: '#fff', px: 4, mt: 4 }}>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                        WELCOME TO TAS ADMIN
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                        Trusted Advisory Service
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1, backgroundColor: 'rgba(76,175,80,0.2)', color: '#4caf50', px: 2, py: 0.5, borderRadius: 1 }}>
                        ✓ Authorized IP: {userIp}
                    </Typography>
                    <Box component="img" src={SignInImg} alt="illustration" sx={{ width: '60%', maxWidth: 400, mt: 4 }} />
                </Grid>
            </Grow>

            {/* Right side */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: "column", justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{ width: '100%', maxWidth: 400, p: 4, pb: 1 }}>
                    <Typography variant="h3" align="center" sx={{ mb: 1 }}>
                        <span style={{ color: "#fff" }}>T</span>
                        <span style={{ color: "#fbb034" }}>A</span>
                        <span style={{ color: "#fff" }}>S</span>
                    </Typography>
                    <Typography align="center" sx={{ color: "#e0e0e0", mb: 2 }}>
                        ADMIN LOGIN
                    </Typography>
                </Box>
                <SignInForm />
            </Grid>
        </Grid>
    );
}